import { type DraftFile, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { VList } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { ImagePreviewModal } from '@/components/modals/ImagePreviewModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { PdfPreviewFrame } from '@/features/artifacts/PdfPreviewFrame';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { MAX_TEXT_PREVIEW_BYTES } from '@/utils/constants';
import {
  formatFileSize,
  truncateFilename,
  getFriendlyMimeName,
  isPastedTextFile,
  isPdfFile,
  isTextPreviewable,
  getFileExtension,
  getDisplayFilename,
} from '@/utils/files';

import { Tap, Tooltip, XStack, YStack } from '../ui';
import { Dialog } from '../ui/dialog';
import { IconButton } from '../ui/IconButton';
import { SaveableField } from '../ui/SaveableField';

const compactFormatter = Intl.NumberFormat('en', { notation: 'compact' });
const formatCompactNumber = (n: number) => compactFormatter.format(n);
const PDF_SIGNATURE = '%PDF-';

interface IFilePreviewProps {
  file: DraftFile;
  removeFile?: () => void;
  /** Called when the user retries a failed upload */
  onRetryFile?: (file: DraftFile) => void;
  /** Makes pasted content editable in its preview dialog; receives the edited text on save. Rejection keeps the dialog open. */
  onSaveText?: (file: DraftFile, text: string) => void | Promise<void>;
  onInsertText?: (file: DraftFile, text: string) => void | Promise<void>;
  isPreviewEnabled: boolean;
}

interface CircularProgressBarProps {
  progress: number; // 0-100
  size?: number;
  strokeWidth?: number;
  className?: string;
}
const CircularProgressBar = ({
  progress,
  size = 40,
  strokeWidth = 4,
  className = '',
}: CircularProgressBarProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <svg
      width={size}
      height={size}
      className={className}
      style={{ display: 'block' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--gray-4)"
        className="text-border"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        className="text-foreground-muted"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s' }}
      />
    </svg>
  );
};

const ImagePreview = observer(
  ({
    file,
    isUploaded,
    isPreviewEnabled,
  }: {
    file: DraftFile;
    isUploaded: boolean;
    isPreviewEnabled: boolean;
  }) => {
    const { drafts: draftsStore } = useStores();
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);

    const isUploading =
      file.uploadState === 'uploading' || file.uploadState === 'pending';
    const progress = file.progress ?? 0;

    useEffect(() => {
      let cancelled = false;
      let localImageUrl: string | undefined = undefined;
      if (isUploaded) {
        draftsStore.getFileUrl(file).then((url) => {
          if (!cancelled) setImageSrc(url);
        });
      } else if (file.file) {
        localImageUrl = URL.createObjectURL(file.file);
        setImageSrc(localImageUrl);
      }
      return () => {
        if (localImageUrl) URL.revokeObjectURL(localImageUrl);
        cancelled = true;
      };
    }, [draftsStore, file, isUploaded]);

    return (
      <div
        onClick={() => {
          if (isPreviewEnabled && imageSrc) {
            ModalsManager.show(ImagePreviewModal, {
              url: imageSrc,
              filename: file.fileName,
            });
          }
        }}
        className={cn(
          'relative flex-none overflow-hidden rounded-md border border-border-strong smoothing-default group size-20',
          isPreviewEnabled && imageSrc ? 'cursor-pointer' : 'cursor-default',
          file.uploadState === 'failed' ? 'border-danger-9' : ''
        )}
      >
        <div className="size-full flex flex-col items-center justify-center gap-2 bg-surface-active relative">
          <AnimatePresence mode="wait">
            {imageSrc ? (
              <motion.img
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                src={imageSrc}
                alt={file.fileName}
                className="aspect-square object-cover rounded-md smoothing-default h-24 w-24"
                loading="lazy"
                onError={(e) => {
                  logError(e, 'Image load error', {
                    context: 'FilePreview',
                    metadata: { file: imageSrc },
                  });
                }}
              />
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <IconButton icon={Icons.imageFile} variant="ghost" disabled />
              </motion.div>
            )}
          </AnimatePresence>
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <CircularProgressBar
                progress={progress}
                size={28}
                strokeWidth={4}
                className="text-white"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

const TextFilePreview = observer(
  ({
    file,
    onSaveText,
    onInsertText,
  }: {
    file: DraftFile;
    onSaveText?: (file: DraftFile, text: string) => void | Promise<void>;
    onInsertText?: (file: DraftFile, text: string) => void | Promise<void>;
  }) => {
    const [textContent, setTextContent] = useState<string>('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasError, setHasError] = useState(false);
    // The user's edits; stays null until the first keystroke so the dialog
    // keeps following textContent while the file is still being read.
    const [draftText, setDraftText] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isInserting, setIsInserting] = useState(false);
    const isUploading =
      file.uploadState === 'uploading' || file.uploadState === 'pending';
    const progress = file.progress ?? 0;

    const isPasted = isPastedTextFile(file.contentType, file.fileName);
    const displayName = getDisplayFilename(file.fileName);
    const badge = isPasted ? 'PASTED' : getFileExtension(file.fileName);
    const dialogTitle = isPasted ? 'Pasted content' : displayName;

    const isTooLarge = file.size > MAX_TEXT_PREVIEW_BYTES;
    // isLoaded gate: editing the not-yet-read content and saving would
    // replace the file with only the keystrokes typed so far
    const canUseLoadedPastedText =
      isPasted && !isUploading && !isTooLarge && !hasError && isLoaded;
    const canEdit = canUseLoadedPastedText && !!onSaveText;
    const canInsert = canUseLoadedPastedText && !!onInsertText;
    // an in-place save flips this file to 'uploading', which would drop
    // canEdit and unmount the editor mid-save; keep it shown while saving
    const showEditor = canEdit || isSaving;

    const lineCount = useMemo(
      () => (textContent ? textContent.split('\n').length : 0),
      [textContent]
    );

    // header stats and the read-only line list deliberately reflect the
    // file, not the in-progress edit, so typing stays O(1); the live char
    // count in the editor footer covers feedback while editing
    const lines = useMemo(
      () => (isOpen && !isTooLarge ? textContent.split('\n') : []),
      [textContent, isOpen, isTooLarge]
    );

    // explicit Save applies; closing or Discard reverts
    const isDirty = draftText !== null && draftText !== textContent;
    const isWhitespaceOnly = draftText !== null && draftText.trim() === '';

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      setDraftText(null);
    };

    const handleSave = async () => {
      if (!isDirty || draftText === null || isWhitespaceOnly || isSaving) {
        return;
      }
      setIsSaving(true);
      try {
        await onSaveText?.(file, draftText);
        setIsOpen(false);
        setDraftText(null);
      } catch {
        // the save path surfaces its own toast; stay open so the edit
        // survives for a retry
      } finally {
        setIsSaving(false);
      }
    };

    const handleInsert = async () => {
      if (!canInsert || isInserting) return;
      setIsInserting(true);
      try {
        await onInsertText?.(file, textContent);
      } catch {
        // The composer path surfaces its own toast
      } finally {
        setIsInserting(false);
      }
    };

    useEffect(() => {
      if (!file.file || isTooLarge) return;
      let cancelled = false;
      // a replaced file starts a fresh read: stale content and a sticky
      // error from the previous file must not survive it
      setIsLoaded(false);
      setHasError(false);
      setTextContent('');
      file.file
        .text()
        .then((text) => {
          if (!cancelled) {
            setTextContent(text);
            setIsLoaded(true);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            logError(error, 'Failed to read text file', {
              context: 'TextFilePreview',
              metadata: { fileName: file.fileName },
            });
            setHasError(true);
          }
        });
      return () => {
        cancelled = true;
      };
    }, [file.file, file.fileName, isTooLarge]);

    return (
      <>
        <YStack
          onClick={() => handleOpenChange(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpenChange(true);
            }
          }}
          aria-label={`View ${isPasted ? 'pasted content' : displayName}`}
          className={cn(
            'group',
            'bg-surface-active',
            'p-2',
            'h-20 w-44',
            'relative',
            'border border-border-strong',
            'rounded-md',
            'smoothing-default',
            'overflow-hidden',
            'select-none',
            'cursor-default',
            {
              'border-danger-9': file.uploadState === 'failed',
            }
          )}
        >
          <div className="flex-1 overflow-hidden mask-linear mask-from-100 mask-via-100 mask-to-0 mask-point-via-[calc(100%-16px)]">
            <p className="text-xs text-foreground-muted group-hover:text-foreground leading-tight transition-colors">
              {hasError ? 'Failed to load preview' : textContent.slice(0, 200)}
            </p>
          </div>
          <XStack className="items-center gap-1.5">
            <span className="h-6 text-xs font-medium text-foreground-subtle border border-border-strongest px-1.5 py-0.5 rounded">
              {badge}
            </span>
            {canInsert && (
              <IconButton
                size="xs"
                icon={Icons.edit}
                type="button"
                disabled={isInserting}
                isLoading={isInserting}
                className="opacity-0 touch:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleInsert();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                aria-label="Edit"
                tooltip="Edit"
              />
            )}
            {isUploading && (
              <CircularProgressBar
                progress={progress}
                size={12}
                strokeWidth={2}
                className="text-foreground"
              />
            )}
            {lineCount > 0 && (
              <span className="ml-auto text-xs tabular-nums text-foreground-muted truncate">
                {`${formatCompactNumber(lineCount)} line${lineCount > 1 ? 's' : ''}`}
              </span>
            )}
          </XStack>
        </YStack>

        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
          <Dialog.Content className="max-w-2xl max-h-[80vh] flex flex-col">
            <Dialog.Header>
              <Dialog.Title className="truncate">
                {truncateFilename(dialogTitle, 40)}
              </Dialog.Title>
              <Dialog.Description>
                {formatFileSize(file.size)}
                {!isTooLarge &&
                  ` · ${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`}
                {isPasted && ' · Formatting may be inconsistent from source'}
              </Dialog.Description>
            </Dialog.Header>
            <div className="h-[60vh]">
              {isTooLarge || hasError ? (
                <div className="h-full flex items-center justify-center text-sm text-foreground-muted rounded-md border border-border-strong bg-surface">
                  {hasError
                    ? 'Failed to load file'
                    : 'File too large to preview'}
                </div>
              ) : showEditor ? (
                <SaveableField
                  id={`pasted-content-${file.id}`}
                  value={draftText ?? textContent}
                  dirty={isDirty}
                  saveDisabled={isWhitespaceOnly}
                  busy={isSaving}
                  wrapperClassName="h-full mt-0"
                  className="h-full text-sm font-mono resize-none scrollbar-hover"
                  onChange={setDraftText}
                  onDiscard={() => setDraftText(null)}
                  onSave={handleSave}
                  statusContent={
                    isSaving ? (
                      <span className="text-xs text-foreground-muted">
                        Saving...
                      </span>
                    ) : isWhitespaceOnly ? (
                      <span className="text-xs text-danger-11">
                        Pasted content can&apos;t be empty. Remove the
                        attachment instead.
                      </span>
                    ) : (
                      <span className="text-xs text-foreground-muted">
                        {`${(draftText ?? textContent).length.toLocaleString()} characters`}
                      </span>
                    )
                  }
                />
              ) : (
                <VList
                  className={cn(
                    'h-full text-sm font-mono p-4 rounded-md border border-border-strong bg-surface',
                    'scrollbar-hover',
                    'mask-linear mask-from-100 mask-via-100 mask-to-0 mask-point-via-[95%]',
                    'cursor-text select-text'
                  )}
                >
                  {lines.map((line, i) => (
                    <div
                      key={i}
                      className="text-foreground whitespace-pre-wrap break-words min-h-[1.5em]"
                    >
                      {line || '\u00A0'}
                    </div>
                  ))}
                </VList>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Root>
      </>
    );
  }
);

const FileAttachmentPreview = observer(({ file }: { file: DraftFile }) => {
  const fileSize = file.size ? formatFileSize(file.size) : '';
  const isUploading =
    file.uploadState === 'uploading' || file.uploadState === 'pending';
  const progress = file.progress ?? 0;
  return (
    <XStack
      className={cn(
        'bg-surface-active',
        'gap-3 items-center',
        'px-3 pr-6',
        'h-20 max-w-56',
        'relative',
        'border border-border-strong',
        'rounded-md',
        'smoothing-default',
        'overflow-hidden',
        'cursor-default',
        {
          'border-danger-9': file.uploadState === 'failed',
        }
      )}
    >
      <div className="relative">
        <YStack
          className={cn(
            'items-center justify-center size-10 rounded-md smoothing-default transition-colors',
            {
              'bg-surface-hover': !isUploading,
            }
          )}
        >
          {!isUploading && (
            <Icons.file className="size-5 text-gray-12 motion-opacity-in-0 motion-scale-in-95" />
          )}
        </YStack>
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <CircularProgressBar
              progress={progress}
              size={28}
              strokeWidth={4}
              className="text-foreground"
            />
          </div>
        )}
      </div>
      <YStack className="flex-1 min-w-0">
        <div className="text-sm truncate" title={file.fileName}>
          {truncateFilename(file.fileName)}
        </div>
        <div className="text-xs text-foreground-subtle truncate">
          {getFriendlyMimeName(file.file?.type ?? 'file', file.fileName)}
        </div>
        <div className="text-xs text-foreground-muted truncate">{fileSize}</div>
      </YStack>
    </XStack>
  );
});

const DraftPdfPreview = observer(
  ({
    file,
    isPreviewEnabled,
  }: {
    file: DraftFile;
    isPreviewEnabled: boolean;
  }) => {
    const [url, setUrl] = useState('');
    const displayName = getDisplayFilename(file.fileName);

    useEffect(() => {
      let localPdfUrl: string | undefined;
      let cancelled = false;
      const pdfFile = file.file;
      setUrl('');

      if (isPreviewEnabled && pdfFile) {
        void pdfFile
          .slice(0, PDF_SIGNATURE.length)
          .text()
          .then((signature) => {
            if (cancelled || signature !== PDF_SIGNATURE) return;

            const pdfBlob = new Blob([pdfFile], { type: 'application/pdf' });
            localPdfUrl = URL.createObjectURL(pdfBlob);
            setUrl(localPdfUrl);
          })
          .catch((error) => {
            if (cancelled) return;
            logError(error, 'Failed to validate PDF file', {
              context: 'DraftPdfPreview',
              metadata: { fileName: file.fileName },
            });
          });
      }

      return () => {
        cancelled = true;
        if (localPdfUrl) URL.revokeObjectURL(localPdfUrl);
      };
    }, [file.file, file.fileName, isPreviewEnabled]);

    return (
      <Dialog.Root>
        {isPreviewEnabled ? (
          <Dialog.Trigger
            aria-label={`Preview ${file.fileName}`}
            disabled={!url}
            className={cn(
              'block rounded-md text-left',
              'cursor-pointer disabled:cursor-default [&>*]:cursor-inherit',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong'
            )}
          >
            <FileAttachmentPreview file={file} />
          </Dialog.Trigger>
        ) : (
          <FileAttachmentPreview file={file} />
        )}
        <Dialog.Content className="max-w-4xl max-h-[85vh] flex flex-col">
          <Dialog.Header>
            <Dialog.Title className="truncate">
              {truncateFilename(displayName, 40)}
            </Dialog.Title>
            <Dialog.Description>{formatFileSize(file.size)}</Dialog.Description>
          </Dialog.Header>
          <div className="h-[70vh]">
            <PdfPreviewFrame
              title={`Preview ${displayName}`}
              url={url}
              className="h-full w-full rounded-md border border-border-strong bg-white"
            />
          </div>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);

export const FilePreview = observer(
  ({
    file,
    removeFile,
    onRetryFile,
    onSaveText,
    onInsertText,
    isPreviewEnabled,
  }: IFilePreviewProps) => {
    const isImage = file.contentType.startsWith('image/');
    const isPdf = isPdfFile(file.contentType, file.fileName);
    const isTextPreview = isTextPreviewable(file.contentType, file.fileName);
    const isFailed = file.uploadState === 'failed';
    const isUploaded = file.uploadState === 'uploaded';

    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: {
            type: 'spring',
            damping: 15,
          },
        }}
        exit={{
          opacity: 0,
          y: 20,
          transition: {
            type: 'keyframes',
            duration: 0.1,
          },
        }}
        className="relative h-20 min-h-20"
      >
        <motion.div>
          {removeFile && (
            <Tap className="absolute top-1 right-1 z-40">
              <IconButton
                size="xs"
                className="bg-surface w-5 h-5 flex items-center justify-center text-xs"
                icon={Icons.cancel}
                onClick={removeFile}
              />
            </Tap>
          )}
          {isImage ? (
            <ImagePreview
              file={file}
              isUploaded={isUploaded}
              isPreviewEnabled={isPreviewEnabled}
            />
          ) : isPdf ? (
            <DraftPdfPreview file={file} isPreviewEnabled={isPreviewEnabled} />
          ) : isTextPreview ? (
            <TextFilePreview
              file={file}
              onSaveText={onSaveText}
              onInsertText={onInsertText}
            />
          ) : (
            <FileAttachmentPreview file={file} />
          )}

          {isFailed && (
            <Tooltip label="Upload failed">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-danger-1/80 z-10">
                <span className="px-2 text-center text-xs break-words">
                  {file.error || 'Upload failed'}
                </span>
                {onRetryFile && (
                  <Tap>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={Icons.refresh}
                      type="button"
                      aria-label="Retry upload"
                      onClick={() => onRetryFile(file)}
                    />
                  </Tap>
                )}
              </div>
            </Tooltip>
          )}
        </motion.div>
      </motion.div>
    );
  }
);
